import { useFishingDiscipline, disciplineLabel } from "../types/fishingDiscipline";
import React, { useState, useEffect } from "react";
import { Gara } from "../types/gare/gara";
import { GaraService } from "../services/garaService";
import {
  DataGrid,
  DataGridColumn,
  DataGridAction,
  FormModal,
  FormField,
  GaraDetailModal,
} from "../components";
import { CampionatiService } from "../services/campionatiService";
import { Campionato } from "../types/campionati/campionato";
import { ApiResponse } from "../types/apiResponse";

// Static gara types
const GARA_TYPES = [
  { value: 1, label: "Gara Individuale" },
  { value: 2, label: "Gara a Coppie" },
  { value: 3, label: "Gara a Box" },
];

const DashboardPage: React.FC = () => {
  const discipline = useFishingDiscipline();
  const [gare, setGare] = useState<ApiResponse<Gara>>({} as ApiResponse<Gara>);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGara, setEditingGara] = useState<Gara | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedGara, setSelectedGara] = useState<Gara | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Debounce searchTerm
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    const loadGare = async () => {
      try {
        setLoading(true);
        let orderBy: "Name" | "CompetitionDate" | "Location" | undefined =
          undefined;
        if (sortField === "name") orderBy = "Name";
        else if (sortField === "competitionDate") orderBy = "CompetitionDate";
        else if (sortField === "location") orderBy = "Location";
        const data = await GaraService.getGareList(
          currentPage,
          pageSize,
          debouncedSearchTerm,
          orderBy,
          sortDirection === "desc",
          discipline
        );
        setGare(data);
        setError(null);
      } catch (err) {
        setError("Errore nel caricamento delle gare");
        console.error("Error loading gare:", err);
      } finally {
        setLoading(false);
      }
    };
    loadGare();
  }, [currentPage, pageSize, debouncedSearchTerm, sortField, sortDirection, discipline]);

  const [campionati, setCampionati] = useState<Campionato[]>([]);

  useEffect(() => {
    CampionatiService.getCampionatiList(1, 1000).then((res) => {
      if (res && Array.isArray(res.results)) {
        setCampionati(res.results);
      } else if (Array.isArray(res)) {
        setCampionati(res);
      } else {
        setCampionati([]);
      }
    });
  }, []);

  // Form fields for FormModal
  const formFields: FormField<Gara>[] = [
    { key: "discipline", label: "Disciplina", type: "select", required: true, defaultValue: discipline,
      options: [{ value: 1, label: "Trout Area" }, { value: 2, label: "Carpe e Carassi" }] },
    {
      key: "name",
      label: "Nome Gara",
      type: "text",
      required: true,
      placeholder: "Inserisci il nome della gara",
    },
    {
      key: "typeId",
      label: "Tipo Gara",
      type: "select",
      required: true,
      defaultValue: discipline === 2 ? 1 : "",
      options: data => (Number(data.discipline) === 2 ? GARA_TYPES.slice(0, 1) : GARA_TYPES).map(t => ({ value: t.value, label: t.label })),
    },
    {
      key: "competitionDate",
      label: "Data",
      type: "date",
      required: true,
      placeholder: "Seleziona la data",
    },
    {
      key: "location",
      label: "Luogo",
      type: "text",
      required: true,
      placeholder: "Inserisci il luogo della gara",
    },
    {
      key: "locandina",
      label: "Locandina",
      type: "file",
      required: false,
      placeholder: "Inserisci la locandina della gara",
    },
    {
      key: "leagueId",
      label: "Campionato",
      type: "select",
      required: false,
      options: data => [
        { value: "", label: "Nessun campionato" },
        ...campionati.filter(c => (c.discipline || 1) === Number(data.discipline || discipline)).map((c) => ({
          value: String(c.id),
          label: `${c.name} (${c.year})`,
        })),
      ],
    },
  ];

  // Handler functions
  const handleCreateGara = () => {
    setEditingGara(null);
    setIsModalOpen(true);
  };

  const handleEditGara = (gara: Gara) => {
    setEditingGara(gara);
    setIsModalOpen(true);
  };

  const handleViewDetails = async (gara: Gara) => {
    try {
      const detailedGara = await GaraService.getGaraById(gara.id);
      setSelectedGara(detailedGara);
    } catch (err) {
      console.error("Errore nel caricamento dettagli gara:", err);
    }
  };

  const handleDeleteGara = async (gara: Gara) => {
    if (
      window.confirm(`Sei sicuro di voler eliminare la gara "${gara.name}"?`)
    ) {
      try {
        await GaraService.deleteGara(gara.id);
        // Remove from local state
        setGare((prev) => ({
          ...prev,
          results: (prev.results || []).filter((g) => g.id !== gara.id),
        }));
        console.log("Gara eliminata con successo");
        // TODO: Show success notification
      } catch (err) {
        console.error("Errore nell'eliminazione della gara:", err);
        // TODO: Show error notification
      }
    }
  };

  const reloadGare = async () => {
    try {
      setLoading(true);
      let orderBy: "Name" | "CompetitionDate" | "Location" | undefined =
        undefined;
      if (sortField === "name") orderBy = "Name";
      else if (sortField === "competitionDate") orderBy = "CompetitionDate";
      else if (sortField === "location") orderBy = "Location";
      const data = await GaraService.getGareList(
        currentPage,
        pageSize,
        debouncedSearchTerm,
        orderBy,
        sortDirection === "desc",
        discipline
      );
      setGare(data);
      setError(null);
    } catch (err) {
      setError("Errore nel caricamento delle gare");
      console.error("Error loading gare:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData: Gara) => {
    try {
      setModalLoading(true);
      // Gestione leagueId: se stringa vuota, undefined, 0 o assente, invia null
      let leagueId: number | null = null;
      if (
        formData.leagueId === undefined ||
        formData.leagueId === null ||
        (typeof formData.leagueId === "string" && formData.leagueId === "") ||
        (typeof formData.leagueId === "number" && formData.leagueId === 0)
      ) {
        leagueId = null;
      } else {
        leagueId = Number(formData.leagueId);
      }
      // Set type string from typeId
      const typeObj = GARA_TYPES.find(
        (t) => t.value === Number(formData.typeId)
      );
      const type = typeObj ? typeObj.label : "";

      // Se c'è un file, usa FormData
      let dataToSend: any;
      const hasFile = formData.locandina && formData.locandina instanceof File;
      //if (hasFile) {
        dataToSend = new FormData();
        dataToSend.append("discipline", String(formData.discipline || discipline));
        dataToSend.append("name", formData.name);
        dataToSend.append("typeId", formData.typeId);
        dataToSend.append("competitionDate", formData.competitionDate);
        dataToSend.append("location", formData.location);
        dataToSend.append("type", type);
        if (leagueId !== null) dataToSend.append("leagueId", leagueId);
        if (hasFile) dataToSend.append("locandina", formData.locandina);
      //} else {
      //  dataToSend = { ...formData, leagueId, type };
      //}

      if (editingGara) {
        await GaraService.updateGara(editingGara.id, dataToSend);
      } else {
        await GaraService.createGara(dataToSend);
      }
      setIsModalOpen(false);
      await reloadGare();
      // TODO: Show success notification
    } catch (err) {
      console.error("Error saving gara:", err);
      throw err;
    } finally {
      setModalLoading(false);
    }
  };

  const columns: DataGridColumn<Gara>[] = [
    {
      key: "name",
      label: "Nome Gara",
      sortable: true,
    },
    {
      key: "competitionDate",
      label: "Data",
      sortable: true,
    },
    {
      key: "location",
      label: "Luogo",
      sortable: true,
    },
    {
      key: "type",
      label: "Tipo Gara",
      sortable: false,
      render: (value) => (
        <span className="text-gray-700 text-sm font-normal">
          {value ?? <span className="text-gray-400">—</span>}
        </span>
      ),
    },
  ];

  const actions: DataGridAction<Gara>[] = [
    {
      label: "Dettagli",
      onClick: handleViewDetails,
    },
    {
      label: "Modifica",
      onClick: handleEditGara,
    },
    {
      label: "Elimina",
      onClick: handleDeleteGara,
      variant: "danger",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Gare · {disciplineLabel(discipline)}</h1>
        <button
          onClick={handleCreateGara}
          className="bg-brand-700 hover:bg-brand-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
        >
          Nuova Gara
        </button>
      </div>

      {/* Loader centrale */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-brand-500"></div>
        </div>
      ) : (
        <DataGrid
          data={gare.results || []}
          columns={columns}
          actions={actions}
          searchPlaceholder="Cerca per nome, data o luogo..."
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={(field, direction) => {
            setSortField(field);
            setSortDirection(direction);
          }}
          emptyStateTitle="Nessuna gara"
          emptyStateDescription="Inizia aggiungendo una nuova gara."
          noResultsTitle="Nessun risultato"
          noResultsDescription="Prova a modificare i termini di ricerca."
          loading={loading}
          error={error}
          pageSize={pageSize}
          showPagination={true}
          currentPage={currentPage}
          totalPages={gare.totalPages || 1}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Form Modal */}
      <FormModal<Gara>
        isOpen={isModalOpen}
        title={editingGara ? "Modifica Gara" : "Nuova Gara"}
        data={editingGara}
        fields={formFields}
        transformValues={(data, key) => key === "discipline" ? { ...data, leagueId: null, typeId: Number(data.discipline) === 2 ? 1 : data.typeId } : data}
        onSubmit={handleFormSubmit}
        onCancel={() => setIsModalOpen(false)}
        loading={modalLoading}
        submitLabel={editingGara ? "Aggiorna" : "Crea"}
      />

      {/* Detail Modal */}
      {selectedGara && (
        <GaraDetailModal
          gara={selectedGara}
          onClose={() => setSelectedGara(null)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
